<template>
    <div
        class="scheduled-email-listing-clip"
        :class="{ 'scheduled-email-listing-clip--new': useNewUi }"
    >
        <div class="scheduled-email-listing">
            <template v-if="isScheduledEmailsFeatureEnabled">
                <TableLoading v-if="initLoading" />
                <template v-else>
                    <EmailListingSettingsHeader
                        ref="ScheduledEmailsTable"
                        :columns="columns"
                        :totalEmails="pagination.total"
                        class="scheduled-email-listing__settings-header"
                    />
                    <div class="scheduled-email-listing__select-all">
                        <CompassCheckbox
                            :modelValue="true"
                            :label="$t.__('common.actions.select_all')"
                            :checked="areAllSelected"
                            :indeterminate="isCheckboxIndeterminate"
                            @change="handleCheckAll"
                        />
                    </div>
                    <TableLoading v-if="isLoading" />
                    <EmailListingTable
                        v-else
                        :columns="columns"
                        :emails="formattedEmails"
                        :selectedEmails="selectedEmails"
                        class="scheduled-email-listing__table"
                        @update:checkedEmails="handleCheckEmails"
                    />
                    <EmailListingPagination
                        v-if="pagination.total > 0"
                        ref="pagination"
                        :isLoading="isLoading"
                        :pagination="pagination"
                        :displayTotalResults="false"
                        @update-pagination="updatePagination"
                    />
                </template>
            </template>

            <ScheduledEmailsListingMassActions
                :selectedEmails="selectedEmails"
                :isToolbarVisible="selectedEmails.length > 0"
                @closeToolbar="handleCloseMassActions"
                @open:delete="handleOpenBatchDeleteDialog"
            />
            <ScheduledEmailsBatchDeleteConfirmDialog
                v-if="showBatchDeleteDialog"
                :emailsCount="selectedEmails.length"
                :isLoading="isBatchDeleting"
                @confirm="handleBatchDelete"
                @close="handleCloseBatchDeleteDialog"
            />
        </div>
    </div>
</template>
<script>
import EmailListingPagination from './EmailListingPagination.vue';
import TableLoading from 'vcomponents/common/table/TableLoading.vue';
import EmailListingTable from 'vcomponents/email/listings/EmailListingTable.vue';
import EmailListingSettingsHeader from 'vcomponents/email/listings/EmailListingSettingsHeader.vue';
import ScheduledEmailsListingMassActions from 'vcomponents/email/listings/ScheduledEmailsListingMassActions.vue';
import ScheduledEmailsBatchDeleteConfirmDialog from 'vcomponents/email/listings/ScheduledEmailsBatchDeleteConfirmDialog.vue';
import Feature from 'Services/Feature';
import {
    getFilteredEmails,
    deleteEmailById,
    deleteEmailsBatch,
} from 'api/emailEngine/emailEngineService';

export default {
    components: {
        EmailListingPagination,
        EmailListingTable,
        EmailListingSettingsHeader,
        ScheduledEmailsListingMassActions,
        ScheduledEmailsBatchDeleteConfirmDialog,
        TableLoading,
    },
    data() {
        return {
            initLoading: true,
            isLoading: false,
            activeTab: 'all',
            columns: [
                {
                    code: 'participants',
                    isDefault: true,
                    isMovable: false,
                    isVisible: true,
                    label: this.$t.__('email.ScheduledEmailListing.recipients'),
                    order: 0,
                    sortKey: 'participants',
                },
                {
                    code: 'subject',
                    isDefault: true,
                    isMovable: false,
                    isVisible: true,
                    label: this.$t.__('email.ScheduledEmailListing.subject'),
                    order: 2,
                    sortKey: 'subject',
                },
                {
                    code: 'scheduled_at',
                    isDefault: true,
                    isMovable: false,
                    isVisible: true,
                    label: this.$t.__('email.ScheduledEmailListing.scheduled_date'),
                    order: 3,
                    sortKey: 'scheduled_at',
                },
            ],
            currentQueryParams: {
                limit: 25,
                offset: null,
            },
            pagination: {
                limit: 25,
                total: 0,
            },
            emails: [],
            selectedEmails: [],
            showBatchDeleteDialog: false,
            isBatchDeleting: false,
            optimisticTotal: null,
            isUnmounted: false,
        };
    },
    computed: {
        useNewUi() {
            return Feature.isEnabled('new-listing-ui');
        },
        isScheduledEmailsFeatureEnabled() {
            return Feature.isEnabled('email-engine-scheduled-emails');
        },
        formattedEmails() {
            return this.emails.map((email) => {
                return {
                    ...email,
                    participants: email.to.concat(email.cc).concat(email.bcc),
                };
            });
        },
        isCheckboxIndeterminate() {
            return (
                this.selectedEmails.length > 0 &&
                this.selectedEmails.length < this.formattedEmails.length
            );
        },
        areAllSelected() {
            return (
                this.formattedEmails.length > 0 &&
                this.selectedEmails.length === this.formattedEmails.length
            );
        },
    },
    created() {
        this.getScheduledEmails();
        EventBus.on('delete:email', this.handleDelete);
    },
    unmounted() {
        this.isUnmounted = true;
        EventBus.off('delete:email', this.handleDelete);
    },
    methods: {
        handleDelete($event) {
            deleteEmailById($event.id)
                .then(() => {
                    this.$pushToast({
                        close: true,
                        text: this.$t.__('email.ScheduledEmailListing.email_deleted'),
                        type: 'success',
                    });
                    this.selectedEmails = this.selectedEmails.filter(
                        (email) => email.id !== $event.id,
                    );
                    this.refreshAfterDeletion([$event.id]);
                })
                .catch((error) => {
                    this.$pushToast({
                        close: true,
                        text: error,
                        type: 'error',
                    });
                });
        },
        getScheduledEmails(emailIdsToExclude = []) {
            this.isLoading = true;
            return getFilteredEmails(['scheduled'], this.currentQueryParams)
                .then((emails) => {
                    this.emails = emails.data.filter(
                        (email) => !emailIdsToExclude.includes(email.id),
                    );
                    this.pagination = emails.pagination;
                    this.applyOptimisticTotal();
                    this.currentQueryParams = {
                        limit: emails.pagination.limit,
                        offset: this.currentQueryParams.offset,
                    };
                })
                .catch((error) => {
                    this.$pushToast({
                        close: true,
                        text: error,
                        type: 'error',
                    });
                })
                .finally(() => {
                    this.initLoading = false;
                    this.isLoading = false;
                });
        },
        async refreshAfterDeletion(deletedEmailIds) {
            this.optimisticTotal = Math.max(0, this.pagination.total - deletedEmailIds.length);
            await this.getScheduledEmails(deletedEmailIds);

            if (this.emails.length > 0) {
                return;
            }

            // The whole page was deleted.
            if (this.currentQueryParams.offset) {
                // Pages after the first: step back — previous pages are untouched by the deletion.
                this.$refs.pagination?.previousPage();
                return;
            }

            // First page: the remaining emails reappear only once the search index catches up
            // with the deletion, so retry a few times before falling back to the empty state.
            const maxRetries = 5;
            const retryDelay = 500;
            for (
                let attempt = 0;
                attempt < maxRetries &&
                !this.isUnmounted &&
                this.emails.length === 0 &&
                this.pagination.total > 0;
                attempt += 1
            ) {
                this.isLoading = true;
                await new Promise((resolve) => setTimeout(resolve, retryDelay));
                await this.getScheduledEmails(deletedEmailIds);
            }
        },
        applyOptimisticTotal() {
            if (this.optimisticTotal === null) {
                return;
            }
            if (this.pagination.total <= this.optimisticTotal) {
                this.optimisticTotal = null;
            } else {
                this.pagination.total = this.optimisticTotal;
            }
        },
        updatePagination(pagination) {
            this.selectedEmails = [];
            this.currentQueryParams = {
                limit: pagination.limit,
                offset: pagination.offset,
            };
            this.getScheduledEmails();
        },
        handleCheckEmails(emails) {
            this.selectedEmails = emails;
        },
        handleCheckAll() {
            this.handleCheckEmails(this.areAllSelected ? [] : this.formattedEmails);
        },
        handleCloseMassActions() {
            this.selectedEmails = [];
        },
        handleOpenBatchDeleteDialog() {
            this.showBatchDeleteDialog = true;
        },
        handleCloseBatchDeleteDialog() {
            this.showBatchDeleteDialog = false;
        },
        handleBatchDelete() {
            const emailIds = this.selectedEmails.map((email) => email.id);

            this.isBatchDeleting = true;
            deleteEmailsBatch({ emailIds })
                .then(() => {
                    this.$pushToast({
                        close: true,
                        text: this.$t.__('email.ScheduledEmailListing.emails_deleted'),
                        type: 'success',
                    });
                    this.selectedEmails = [];
                    this.handleCloseBatchDeleteDialog();
                    return this.refreshAfterDeletion(emailIds);
                })
                .catch((error) => {
                    this.$pushToast({
                        close: true,
                        text: error,
                        type: 'error',
                    });
                })
                .finally(() => {
                    this.isBatchDeleting = false;
                });
        },
    },
};
</script>
<style lang="scss" scoped>
.scheduled-email-listing-clip {
    display: flex;
    flex-direction: column;
    height: 100%;
    max-height: 100%;
    min-height: 0;

    &--new {
        overflow: hidden;

        .scheduled-email-listing {
            flex: 0 1 auto;
            height: auto;
            min-height: 0;
            position: relative;
        }
    }
}

.scheduled-email-listing {
    display: flex;
    flex-direction: column;
    height: 100%;
    max-height: 100%;
    min-height: 0;
    border-radius: var(--spacing-0);
    padding: 0 var(--spacing-3);
    background: var(--colors-common-background-default);
    transition: max-height 0.3s ease-in-out;

    &:has(.listingBatchToolbar--new) {
        max-height: calc(100% - var(--spacing-7) - var(--spacing-4));
    }

    &__settings-header {
        align-items: center; // to be removed when filters are added to the header
        min-height: 64px;
    }

    &__select-all {
        display: flex;
        justify-content: flex-start;
        align-items: center;
        flex-shrink: 0;
        height: 48px;
        padding: 0 var(--spacing-3);
        background-color: var(--colors-common-background-default);
        border-width: 0px;
        border-bottom-width: 1px;
        border-style: solid;
        border-bottom-color: var(--colors-common-border-lighter);
    }

    &__table {
        padding: 0 0 var(--spacing-2);
        overflow: auto;
    }
}
</style>
